import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const SaveManagerModal = ({ isOpen, onClose, saveManager, currentAppState, onLoad, appState }) => {
    const { t } = useTranslation();
    const [newSaveName, setNewSaveName] = useState('');
    const { saves, createSave, updateSave, loadSave, deleteSave, isLoading, error } = saveManager;
    const [actionStatus, setActionStatus] = useState(null); // 'saving', 'loading', 'deleting', 'success', 'error'
    const MAX_SLOTS = 20;
    const isFull = saves.length >= MAX_SLOTS;

    if (!isOpen) return null;

    const handleCreateSave = async () => {
        if (!newSaveName.trim()) return;

        setActionStatus('saving');
        const success = await createSave(newSaveName, currentAppState);
        if (success) {
            setNewSaveName('');
            setActionStatus('success');
            setTimeout(() => setActionStatus(null), 2000);
        } else {
            setActionStatus('error');
            // Error handling is managed by saveManager.error state
        }
    };

    const handleLoadSave = async (saveId) => {
        if (window.confirm(t('SaveManagerModal.confirm_load'))) {
            setActionStatus('loading');
            const data = await loadSave(saveId);
            if (data) {
                onLoad(data);
                setActionStatus('success');
                onClose(); // Close modal on successful load
            } else {
                setActionStatus('error');
            }
        }
    };

    const handleOverwriteSave = async (saveId, saveName) => {
        if (window.confirm(t('SaveManagerModal.confirm_overwrite', { saveName }))) {
            setActionStatus('saving');
            // We pass saveName to keep the same name, or we could ask for a new name. 
            // For now, simpler to keep same name.
            await updateSave(saveId, saveName, currentAppState);
            setActionStatus('success');
            setTimeout(() => setActionStatus(null), 2000);
        }
    };

    const handleDeleteSave = async (saveId) => {
        if (window.confirm(t('SaveManagerModal.confirm_delete'))) {
            setActionStatus('deleting');
            await deleteSave(saveId);
            setActionStatus(null);
        }
    };

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4 h-[100dvh] w-screen"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-fade-in-up max-h-[90dvh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-primary-green px-6 py-4 flex justify-between items-center shrink-0">
                    <h3 className="text-white text-xl font-bold">{t('SaveManagerModal.title')}</h3>
                    <button onClick={onClose} className="text-white hover:bg-white/20 rounded-full p-1 transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Body */}
                <div className="p-6 overflow-y-auto">
                    {/* Create New Save */}
                    <div className="flex justify-between items-center mb-2">
                        <label className="text-sm font-semibold text-gray-700">{t('SaveManagerModal.label_new_save')}</label>
                        <span className={`text-xs font-bold ${isFull ? 'text-red-500' : 'text-gray-500'}`}>
                            {t('SaveManagerModal.slots_used', { count: saves.length, max: MAX_SLOTS })}
                        </span>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 mb-6">
                        <input
                            type="text"
                            placeholder={t('SaveManagerModal.placeholder_name')}
                            value={newSaveName}
                            onChange={(e) => setNewSaveName(e.target.value)}
                            className="w-full sm:flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-green focus:border-transparent outline-none"
                        />
                        <button
                            onClick={handleCreateSave}
                            disabled={isLoading || !newSaveName.trim() || isFull}
                            className="w-full sm:w-auto bg-primary-green text-white px-4 py-2 rounded-lg font-bold hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                        >
                            {actionStatus === 'saving' ? (
                                <span className="animate-spin">↻</span>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                                </svg>
                            )}
                            {t('SaveManagerModal.button_save')}
                        </button>
                    </div>

                    {/* Status Messages */}
                    {error && <div className="text-red-500 text-sm mb-4 bg-red-50 p-2 rounded">{error}</div>}
                    {actionStatus === 'success' && <div className="text-green-600 text-sm mb-4 bg-green-50 p-2 rounded">{t('SaveManagerModal.msg_success')}</div>}

                    {/* Saves List */}
                    <div className="border-t border-gray-200 pt-4 max-h-80 overflow-y-auto">
                        <h4 className="text-gray-500 text-sm font-bold uppercase tracking-wider mb-3">{t('SaveManagerModal.section_saves')}</h4>

                        {isLoading && saves.length === 0 ? (
                            <div className="text-center py-4 text-gray-400">{t('SaveManagerModal.loading_saves')}</div>
                        ) : saves.length === 0 ? (
                            <div className="text-center py-8 text-gray-400 italic">{t('SaveManagerModal.empty_saves')}</div>
                        ) : (
                            <div className="space-y-3">
                                {saves.map(save => (
                                    <div key={save.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center bg-gray-50 p-3 rounded-lg border border-gray-100 hover:border-primary-green transition-colors group gap-2">
                                        <div className="w-full sm:w-auto">
                                            <div className="font-bold text-gray-800 break-words">{save.name}</div>
                                            <div className="text-xs text-gray-500">{new Date(save.created_at).toLocaleString()}</div>
                                        </div>
                                        <div className="flex gap-2 w-full sm:w-auto justify-end">
                                            <button
                                                onClick={() => handleOverwriteSave(save.id, save.name)}
                                                disabled={isLoading}
                                                className="bg-yellow-500 text-white px-3 py-1.5 rounded text-sm hover:bg-yellow-600 transition-colors shadow-sm flex items-center gap-1"
                                                title={t('SaveManagerModal.tooltip_overwrite')}
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                                </svg>
                                                <span className="hidden sm:inline">{t('SaveManagerModal.button_overwrite')}</span>
                                            </button>
                                            <button
                                                onClick={() => handleLoadSave(save.id)}
                                                disabled={isLoading}
                                                className="bg-blue-500 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-600 transition-colors shadow-sm"
                                            >
                                                {t('SaveManagerModal.button_load')}
                                            </button>
                                            <button
                                                onClick={() => handleDeleteSave(save.id)}
                                                disabled={isLoading}
                                                className="bg-white text-red-500 border border-red-200 px-3 py-1.5 rounded text-sm hover:bg-red-50 transition-colors"
                                            >
                                                {t('SaveManagerModal.button_delete')}
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SaveManagerModal;
// Revisado: 2025-12-18 [I18n Scan]
